<section id="home" class="relative min-h-screen overflow-hidden bg-surface flex flex-col justify-center pt-20 lg:pt-0">
  <!-- Background -->
  <div class="absolute inset-0 -z-10">
    <div class="absolute top-[15%] left-[30%] w-150 h-150 rounded-full bg-accent/3 blur-[200px]"></div>
    <div class="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-subtle/20 to-transparent"></div>
  </div>

  <div class="container mx-auto px-6 lg:px-10 xl:px-14">

    <!-- Eyebrow badge -->
    <div class="flex items-center gap-3 justify-center lg:justify-start mb-10 lg:mb-12" data-aos="fade-up" data-aos-delay="100">
      <span class="relative h-1.5 w-1.5 rounded-full bg-accent animate-glow-pulse"></span>
      <span class="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">{{ 'home.badge' | translate }}</span>
    </div>

    <!-- Split -->
    <div class="flex flex-col lg:flex-row items-center lg:items-center gap-10 lg:gap-16">

      <!-- Left — Content -->
      <div class="w-full lg:flex-3 text-center lg:text-left order-2 lg:order-1">

        <!-- Name -->
        <div data-aos="fade-up" data-aos-delay="250">
          <h1 class="text-[clamp(2.8rem,6.5vw,5.5rem)] font-display font-black text-text-strong leading-[0.92] tracking-[-0.04em]">
            {{ 'home.name' | translate }}
          </h1>

          <!-- Role -->
          <div class="mt-5 flex items-center gap-3 justify-center lg:justify-start">
            <div class="h-px w-10 bg-accent/50 hidden lg:block"></div>
            <p class="font-mono text-[13px] text-accent tracking-wide">{{ 'home.role' | translate }}</p>
          </div>
        </div>

        <!-- Typing -->
        <div class="mt-8 flex items-start gap-3 justify-center lg:justify-start" data-aos="fade-up" data-aos-delay="300">
          <span class="text-accent/40 font-mono text-base leading-[1.6] select-none">&gt;</span>
          <p class="text-base sm:text-lg text-muted-light/80 font-mono min-h-14 sm:min-h-8 leading-relaxed">
            <span id="typing-effect"></span>
          </p>
        </div>

        <!-- CTA -->
        <div class="mt-10 flex flex-col sm:flex-row justify-center lg:justify-start gap-3" data-aos="fade-up" data-aos-delay="400">
          <a [routerLink]="getBasePath()" fragment="projects" role="button" [attr.aria-label]="'HOME.BUTTONS.PROJECTS' | translate"
            class="bg-accent text-text-inverse font-display font-bold py-3 px-8 rounded text-sm tracking-wide transition-all duration-300 hover:bg-accent-dim hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20">
            {{ 'home.buttons.projects' | translate }}
          </a>
          <a [attr.href]="getResumeFilePath()" download role="button" [attr.aria-label]="'HOME.BUTTONS.DOWNLOAD' | translate"
            class="border border-subtle text-muted-light hover:border-accent hover:text-accent hover:-translate-y-0.5 transition-all duration-300 font-display font-bold py-3 px-8 rounded text-sm tracking-wide text-center">
            {{ 'home.buttons.download' | translate }}
          </a>
        </div>

        <!-- Stats -->
        <div class="mt-14 flex flex-wrap items-center justify-center lg:justify-start gap-10" data-aos="fade-up" data-aos-delay="500">
          <div class="flex flex-col">
            <span class="text-[2rem] font-display font-black text-accent leading-none">5+</span>
            <span class="mt-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-muted">{{ 'home.stats.years' | translate }}</span>
          </div>
          <div class="h-8 w-px bg-linear-to-b from-transparent via-subtle to-transparent hidden sm:block"></div>
          <div class="flex flex-col">
            <span class="text-[2rem] font-display font-black text-accent leading-none">10+</span>
            <span class="mt-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-muted">{{ 'home.stats.projects' | translate }}</span>
          </div>
        </div>
      </div>

      <!-- Right — Photo -->
      <div class="lg:flex-2 flex justify-center order-1 lg:order-2" data-aos="fade-left" data-aos-delay="200">
        <div class="relative w-full max-w-70 lg:max-w-[320px]">
          <!-- Photo with decorative corner brackets -->
          <div class="relative rounded-2xl overflow-hidden border border-subtle/50 shadow-2xl shadow-surface/80 group">
            <div class="relative aspect-3/4">
              <img src="images/vinicius-ribeiro-hero.webp" loading="eager" [alt]="'shared.photo.alt' | translate" width="320" height="427"
                class="w-full h-full object-cover object-top brightness-[0.83] transition-all duration-700 group-hover:scale-[1.02] group-hover:brightness-[0.90]">
              <div class="absolute inset-0 bg-surface/10 mix-blend-multiply"></div>
              <div class="absolute inset-0 bg-linear-to-br from-surface/40 via-transparent to-transparent"></div>
              <div class="absolute inset-0 bg-linear-to-t from-surface/80 via-transparent to-transparent"></div>
            </div>
            <!-- Decorative corner accents -->
            <div class="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-accent/40"></div>
            <div class="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-accent/40"></div>
          </div>
          <!-- Status pill -->
          <div class="absolute -bottom-3 left-1/2 -translate-x-1/2">
            <div class="flex items-center gap-2 backdrop-blur-md bg-surface-100/90 border border-subtle rounded-full py-1.5 px-4">
              <span class="relative h-1.5 w-1.5 rounded-full bg-accent animate-glow-pulse"></span>
              <span class="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-light whitespace-nowrap">{{ 'home.availabilityValue' | translate }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom tech bar -->
    <div class="mt-16 lg:mt-20 pb-8 lg:pb-0 flex items-center gap-5 overflow-hidden" data-aos="fade-up" data-aos-delay="600">
      <span class="font-mono text-[10px] text-accent/50 tracking-widest select-none">///</span>
      <p class="font-mono text-[10px] text-muted uppercase tracking-[0.2em] whitespace-nowrap">
        {{ 'home.stackValue' | translate }}
      </p>
      <div class="h-px flex-1 bg-linear-to-r from-subtle/40 to-transparent"></div>
    </div>
  </div>
</section>
